import { mdiSendVariant } from '@mdi/js';
import Icon from '@mdi/react';
import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAgentByIdServiceV2 } from '../../../../services/AgentService';
import { LoadingComponent } from '../../../shared/LoadingComponent';
import { Button } from '../../../ui/button';
import { chatbot } from '../../ai-agent-v2/AgentLayoutComponentV2';
import { MarkDownEditorComponent } from '../MarkDownEditor';
import { ArrowDown, BotIcon, CogIcon } from 'lucide-react';
import { AppAgentContext } from 'src/context/ContextProvider';
import { sbx360icon, uuidV4 } from '../../../../utils';
import { sendMessageServer } from 'src/utils/botGenerator/htmlFormat';
import { ChatChartData } from './ChatChartComponent';
import { FormatMessage } from './FormatMessageComponent';
import { useSearchParams } from '../../../../App';

export enum UserType {
    AGENT = 'agent',
    USER = 'user',
}

export enum TextType {
    TABLE = 'table',
    CHART = 'chart',
    TEXT = 'text',
    WAITING = 'waiting',
    CODE = 'code',
    MARKDOWN = 'markdown',
}

type ContentTable = {
    meta: {
        data: {
            [key: string]: any;
        }[];
        columns: {
            key: string;
            name: string;
            sortable: boolean;
        }[];
        defaultSort: {
            column: string;
            order: string;
        };
    };
};

type ContentText = {
    text: string;
};

type ContentCode = {
    meta: {
        language: 'SQL';
    };
    data: string;
};

interface MessagesTable extends Message {
    type: TextType.TABLE;
    content: ContentTable;
}

interface MessagesText extends Message {
    type: TextType.TEXT | TextType.WAITING;
    content: ContentText;
}

interface MessagesChart extends Message {
    type: TextType.CHART;
    content: ChatChartData;
}

interface MessagesCode extends Message {
    type: TextType.CODE;
    content: ContentCode;
}

export type MessagesBot = MessagesTable | MessagesText | MessagesChart | MessagesCode;

export interface Message {
    message_id: string;
    role: UserType;
}

const sessionID = uuidV4();

export default function MarkdownChatComponent() {
    const { agentId = '0' } = useParams();

    const searchParams = useSearchParams();

    const isPreviewMarkDown = searchParams.has('preview_mark_down_token');

    const [scrollIsDown, setScrollsIsDown] = useState(true);
    const chatBodyRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const {
        state: {
            session: { user_id, name },
        },
    } = useContext(AppAgentContext);

    const { data, isLoading } = useQuery({
        queryKey: ['agent', agentId],
        queryFn: async () => {
            const response = await getAgentByIdServiceV2(agentId);
            return response.item;
        },
    });

    const [messages, setMessages] = useState<MessagesBot[]>([]);
    const [mkdUUID, setMkdUUID] = useState<string>(uuidV4());

    const [inputMKD, setInputMKD] = useState('');

    const moveScroll = () => {
        setTimeout(() => {
            chatBodyRef.current?.scrollTo({ top: chatBodyRef.current.scrollHeight, behavior: 'smooth' });
        }, 200);
    };

    const onSendMessage = (msg?: string) => {
        // const hrMessage: MessagesBot = { from: 'hr', message: ''};
        const message = msg || inputMKD;
        if (message) {
            const messageId = uuidV4();
            const messagesInit: MessagesBot[] = [
                ...messages,
                {
                    message_id: messageId,
                    role: UserType.USER,
                    type: TextType.TEXT,
                    content: {
                        text: message,
                    },
                },
            ];
            setMessages(messagesInit);
            setMkdUUID(uuidV4());
            setInputMKD('');
            const idBot = uuidV4();
            setMessages([
                ...messagesInit,
                {
                    message_id: idBot,
                    role: UserType.AGENT,
                    type: TextType.WAITING,
                    content: {
                        text: '...',
                    },
                },
            ]);
            moveScroll();
            sendMessageServer(user_id, data?.id, sessionID, message).then((res: any) => {
                const message = (res.item.message as string).toJson<MessagesBot[]>();
                if (Array.isArray(message)) {
                    setMessages((prevMessages) => {
                        const baseMessages = [...prevMessages];
                        const userMessageIndex = baseMessages.findIndex(
                            (message) => message.role === UserType.USER && message.message_id === messageId
                        );
                        baseMessages.splice(userMessageIndex + 1, 0, ...message);
                        return baseMessages.filter((it) => it.message_id !== idBot);
                    });
                } else {
                    setMessages((prevMessages) => [
                        ...prevMessages.map((messageP) => {
                            if (messageP.message_id === idBot) {
                                return {
                                    ...messageP,
                                    ...((res.item.message as string).toJson<MessagesBot>() ||
                                        ({
                                            type: TextType.TEXT,
                                            content: {
                                                text: res.item.message ? res.item.message : 'No hubo respuesta, intenta haciendo otra pregunta.',
                                            },
                                            message_id: uuidV4(),
                                        } as MessagesBot)),
                                };
                            }

                            return messageP;
                        }),
                    ]);
                }

                moveScroll();
            });
        }
    };

    //memo suggestions
    const suggestions = useMemo(
        () =>
            data?.look_and_feel_chat.suggested_message.filter(
                (message) =>
                    !messages.some(
                        (messageBot) =>
                            messageBot.role === 'user' && ((messageBot.content as ContentText)?.text ?? '') === message
                    ) || !messages.length
            ) || [],
        [data, messages]
    );

    switch (true) {
        case isLoading:
            return <LoadingComponent />;

        case !data:
            return (
                <div className="flex flex-col justify-center items-center h-screen w-full font-revert">
                    <h1>Agent not found</h1>
                    <Button variant="default" onClick={() => navigate(`/agent_list"`)}>
                        Go back
                    </Button>
                </div>
            );

        default:
            return (
                <div className="w-full h-screen pt-14 p-2 lg:pt-2 overflow-scroll flex justify-center items-center bg-gradient-to-br from-pink-100 to-indigo-100  flex-col font-revert">
                    <button
                        aria-hidden={isPreviewMarkDown}
                        onClick={() => navigate(`/agent_setup/${agentId}`)}
                        className="fixed top-3 left-4 rounded-full flex gap-1 aria-hidden:hidden"
                    >
                        <CogIcon className="w-[20px]" />
                        Setup
                    </button>
                    <a
                        target={'_blank'}
                        href={'https://sbx360.com'}
                        className="fixed top-3 right-4 rounded-full flex gap-1"
                    >
                        <img className={'w-[110px]'} src={sbx360icon} alt="sbx360 icon" />
                    </a>
                    <div className="max-w-[900px] w-full h-[calc(100%_-_30px)] flex flex-col ">
                        {/*HEADER*/}
                        <div className="h-[80px] flex items-center justify-between bg-gradient-primary rounded-[20px] px-2">
                            <h4>{data?.name}</h4>
                            <img
                                className="rounded-full w-[40px]"
                                src={data?.avatar ? data.avatar : chatbot}
                                alt="bot img"
                            />
                        </div>
                        {/*CHAT*/}
                        <div
                            ref={chatBodyRef}
                            onScroll={(event) => {
                                const { scrollTop, scrollHeight, clientHeight } = event.target as HTMLDivElement;
                                if (scrollTop + clientHeight >= scrollHeight - 200) {
                                    setScrollsIsDown(true);
                                } else {
                                    setScrollsIsDown(false);
                                }
                            }}
                            className="h-full w-full p-10 overflow-scroll relative"
                        >
                            <div className="flex flex-col gap-5">
                                {!messages.length && (
                                    <div className="opacity-60">
                                        <div className="flex justify-center items-center gap-2 text-2xl text-gray-500">
                                            Welcome to {data?.name} <BotIcon className="font-bold text-2xl" />
                                        </div>
                                        <p className="text-center text-gray-500">
                                            Ask me anything, I am here to help you.
                                        </p>
                                    </div>
                                )}

                                {messages.map((message) => {
                                    switch (message.role) {
                                        case UserType.AGENT:
                                            return (
                                                <div
                                                    aria-readonly={
                                                        ((message.content as ContentText)?.text ?? '') === '...'
                                                    }
                                                    key={message.message_id}
                                                    className="flex gap-5 my-2 animate-iteration-one aria-readonly:animate-none"
                                                >
                                                    <div className="w-[40px] h-[40px]">
                                                        <img
                                                            className="rounded-full w-[50px]"
                                                            src={data?.avatar ? data.avatar : chatbot}
                                                            alt="bot img"
                                                        />
                                                    </div>

                                                    <FormatMessage message={message} data={data!} />
                                                </div>
                                            );

                                        case UserType.USER:
                                            return (
                                                <div
                                                    key={message.message_id}
                                                    className="flex justify-end gap-5 my-2 animate-iteration-one"
                                                >
                                                    <FormatMessage message={message} data={data!} />
                                                    <div
                                                        className={`w-[40px] h-[40px] border-4 border-white p-0 outline-none transition-all rounded-full flex items-center justify-center text-[white] name-${name.slice(0, 1).toUpperCase()}`}
                                                    >
                                                        {name.slice(0, 1).toUpperCase()}
                                                    </div>
                                                </div>
                                            );

                                        default:
                                            return null;
                                    }
                                })}
                            </div>
                            <span
                                aria-hidden={scrollIsDown}
                                onClick={moveScroll}
                                className="h-[40px] w-[40px] bg-gradient-primary rounded-full flex items-center justify-center text-white sticky bottom-0 left-[calc(50%-20px)] animate-bounce cursor-pointer select-none aria-hidden:hidden"
                            >
                                <ArrowDown />
                            </span>
                        </div>

                        <div className="p-2 w-full flex flex-wrap items-center justify-between px-10 gap-2 bg-white rounded-[20px]">
                            <div aria-hidden={!suggestions.length} className="p-2 aria-hidden:hidden">
                                {suggestions.map((message) => {
                                    return (
                                        <button
                                            key={message}
                                            onClick={async () => {
                                                onSendMessage(message);
                                            }}
                                            className="hover:bg-gray-200 transition-all rounded-[10px] shadow m-1 p-2"
                                        >
                                            {message}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="w-[calc(100%-60px)]">
                                <MarkDownEditorComponent
                                    key={mkdUUID}
                                    className="w-full h-full"
                                    markdown={inputMKD}
                                    placeholder={'Type your question here!'}
                                    onSendMessageEnter={onSendMessage}
                                    onChange={setInputMKD}
                                />
                            </div>
                            <button
                                onClick={() => onSendMessage()}
                                className=" hover:scale-105 transition-all text-primary"
                            >
                                <Icon path={mdiSendVariant} className="ml-1" size={2} />
                            </button>
                        </div>
                    </div>
                    <footer className="h-[30px] flex justify-center items-center">
                        <p className="text-center text-gray-400 text-xs m-0 ">
                            SBX360 &copy; 2025 <span className="text-gray-600">AI</span> All Rights Reserved
                        </p>
                    </footer>
                </div>
            );
    }
}
